import React, { createContext, Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Control,
  useForm,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormWatch
} from 'react-hook-form';
import { AddressSearchService } from 'services/AddressSearchService'; // Used to search for addresses
import store from 'store'; // Used to access the store
import { AssistedAddressFilter } from 'tools/AddressFilter'; // Used to filter the address options

// Defines the type of the option
export type Option = string | Record<string, any>;

// Defines the data structure of the form
interface FormData {
  street: string;
  houseNumber: string;
  zip: string;
  city: string;
}

// Defines the address options
export interface AddressOptions {
  postalCode: Option[];
  municipality: Option[];
  street: Option[];
  houseNumber: Option[];
}

// Defines the type of the context
interface AssistedAddressSearchContextType {
  options: AddressOptions; // Stores the address options
  selectedOptions: AddressOptions; // Stores the selected address options
  copyInvoiceAddressChecked: boolean;
  setOptions: Dispatch<SetStateAction<AddressOptions>>; // Sets the address options
  setSelectedOptions: Dispatch<SetStateAction<AddressOptions>>; // Sets the selected address options
  setCopyInvoiceAddressChecked: Dispatch<SetStateAction<boolean>>;

  // React hook form
  errors: any; // Stores the errors
  formControl: Control<FormData, any>; // Controls the form elements
  setValue: UseFormSetValue<FormData>; // Sets the value of the form elements
  handleSubmit: UseFormHandleSubmit<FormData>; // Handles the submit event of the form

  formData: UseFormWatch<FormData>;
  streetTypeaheadAllowed: boolean;
}

// Creates an instance of the address search service
const addressSearch = new AddressSearchService();

// Creates a context object
export const AssistedAddressSearchContext = createContext({} as AssistedAddressSearchContextType);

// The provider component
export const AssistedAddressSearchProvider: React.FC = ({ children }) => {
  // Manages the loading state
  const [loading, setLoading] = useState<boolean>(false);
  const [streetTypeaheadAllowed, setStreetTypeaheadAllowed] = useState<boolean>(true);

  const [copyInvoiceAddressChecked, setCopyInvoiceAddressChecked] = useState<boolean>(false);

  // Manages the selected address options
  const [selectedOptions, setSelectedOptions] = useState<AddressOptions>({
    postalCode: [],
    municipality: [],
    street: [],
    houseNumber: []
  });

  // Manages the address options
  const [options, setOptions] = useState<AddressOptions>({
    postalCode: [],
    municipality: [],
    street: [],
    houseNumber: []
  });

  // Initializes the react hook form
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: store.getState().onboardingInfo.houseAddress
  });

  const sendRequest = () => {
    // Gets the values of the form elements
    const watchFields = watch();
    setStreetTypeaheadAllowed(watchFields.zip.length < 5);

    // Creates an object with the form values
    const searchObject = {
      postalCode: watchFields.zip || '',
      municipality: watchFields.city || '',
      streetName: watchFields.street || '',
      streetNumber: watchFields.houseNumber || ''
    };

    // Checks if municipality, postalcode and street are set
    const allRequirementsSet =
      searchObject.municipality.length > 0 &&
      searchObject.postalCode.length > 0 &&
      searchObject.streetName.length > 0;

    // Sends the request if the loading flag is true and the requirements are met
    if (allRequirementsSet && !copyInvoiceAddressChecked) {
      addressSearch
        .search(searchObject, 'structured')
        .then((result: any) => {
          const postalCodeOptions = AssistedAddressFilter.findPostalCodeOptions(watchFields.zip)
            .map((elem: { postalCode: string }) => elem.postalCode)
            .filter(
              (postalCode: string, pos: any, self: string | any[]) =>
                self.indexOf(postalCode) === pos // remove duplicate entries
            );

          // // Filters the address options

          const streetOptions = AssistedAddressFilter.findStreetOptions(
            result,
            searchObject.postalCode,
            searchObject.municipality
          );

          let uniqueOptions = [...streetOptions, ...options.street].filter((c: any, index: any) => {
            return streetOptions.indexOf(c) === index;
          });

          // Sets the address options
          options.street = uniqueOptions;
          options.postalCode = postalCodeOptions;
          setOptions(options);
        })
        .catch(() => {
          // Resets all options if something goes wrong
          setOptions({
            postalCode: [],
            municipality: [],
            street: [],
            houseNumber: []
          });
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  };

  // Watches for changes in the form elements
  useEffect(() => {
    // Detects changes of react form hook
    const subscription = watch(() => {
      setLoading(true);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Sends request based on the loading flag
  useEffect(() => {
    if (!loading) return;

    sendRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, watch]);

  // Renders the provider component
  return (
    <AssistedAddressSearchContext.Provider
      value={{
        options,
        setOptions,
        selectedOptions,
        setSelectedOptions,
        copyInvoiceAddressChecked,
        setCopyInvoiceAddressChecked,

        errors,
        setValue,
        handleSubmit,
        formControl: control,

        streetTypeaheadAllowed,
        formData: watch
      }}
    >
      {children}
    </AssistedAddressSearchContext.Provider>
  );
};
